import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "../fragments/Image";

const PageSplash = ({ block }) => {
    const [active, setActive] = useState(0);
    const [isPortrait, setIsPortrait] = useState(false);
    const intervalRef = useRef(null);
    const videoRefs = useRef([]);
    const altVideoRefs = useRef([]);

    const pageSplash = useRef(null);

    const handleResize = useCallback(() => {
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        setIsPortrait(windowHeight > windowWidth);
    }, []);

    const getMediaSrc = useCallback((slide, type = 'src') => {
        // Check if we want video or image
        const isVideo = type === 'video';
        
        // Get orientation-specific media
        const portraitMedia = isVideo ? slide.portrait_video : slide.portrait_src;
        const landscapeMedia = isVideo ? slide.landscape_video : slide.landscape_src;
        
        // Return appropriate media based on orientation, with fallbacks
        if (isPortrait) {
            return portraitMedia || landscapeMedia || slide.src;
        } else {
            return landscapeMedia || portraitMedia || slide.src;
        }
    }, [isPortrait]);

    const getAltMediaSrc = useCallback((slide, type = 'src') => {
        const isVideo = type === 'video';
        const portraitMedia = isVideo ? slide.dark_portrait_video : slide.dark_portrait_src;
        const landscapeMedia = isVideo ? slide.dark_landscape_video : slide.dark_landscape_src;

        if (isPortrait) {
            return portraitMedia || landscapeMedia || null;
        }

        return landscapeMedia || portraitMedia || null;
    }, [isPortrait]);

    const hasVideo = useCallback((slide) => {
        return !!(slide.portrait_video || slide.landscape_video);
    }, []);

    const hasAltVideo = useCallback((slide) => {
        return !!(slide.dark_portrait_video || slide.dark_landscape_video);
    }, []);

    const hasAltMedia = useCallback((slide) => {
        return !!(
            slide.dark_portrait_src ||
            slide.dark_landscape_src ||
            slide.dark_portrait_video ||
            slide.dark_landscape_video
        );
    }, []);

    const playActiveVideo = useCallback(() => {
        const handleVideo = (video, index) => {
            if (!video) return;

            if (index === active) {
                // Reset and play active video
                video.currentTime = 0;
                video.play().catch(error => {
                    console.log('Video autoplay failed:', error);
                });
            } else {
                // Pause inactive videos
                video.pause();
            }
        };

        videoRefs.current.forEach(handleVideo);
        altVideoRefs.current.forEach(handleVideo);
    }, [active]);

    useEffect(() => {
        // Set initial orientation
        handleResize();

        // Add resize listener
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        // Early return if no slides or only one slide
        if (!block?.slides || block.slides.length <= 1) return;

        // Start slideshow after initial delay
        const timeoutId = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                if (pageSplash.current) {
                    setActive(prevActive => {
                        return (prevActive + 1) % block.slides.length;
                    });
                }
            }, 4000);
        }, 2400);

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [block?.slides?.length]);

    // Play video when active slide changes
    useEffect(() => {
        // Add small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            playActiveVideo();
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [active, playActiveVideo]);

    // Early returns for error states
    if (!block) return null;
    if (!block.slides) return null;

    return (
        <div className="c-page__block c-page__block--splash" ref={pageSplash}>
            <div className="c-splash">
                <div className="c-splash__slides">
                    {block.slides.map((slide, i) => {
                        const videoSrc = getMediaSrc(slide, 'video');
                        const imageSrc = getMediaSrc(slide, 'src');
                        const showVideo = hasVideo(slide);
                        const altVideoSrc = getAltMediaSrc(slide, 'video');
                        const altImageSrc = getAltMediaSrc(slide, 'src');
                        const showAltVideo = hasAltVideo(slide);
                        const hasAlt = hasAltMedia(slide);

                        return (
                            <div
                                className={`c-splash__slide ${
                                    i === active ? "state-active" : ""
                                } ${hasAlt ? 'state-has-alt-media' : ''}`}
                                key={i}
                            >
                                <div className="c-splash__slide__inner">
                                    {showVideo && videoSrc ? (
                                        <video
                                            ref={el => videoRefs.current[i] = el}
                                            className="c-splash__slide__video"
                                            src={videoSrc}
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                            onCanPlay={() => {
                                                console.log(`Video ${i} can play`);
                                                if (i === active) {
                                                    playActiveVideo();
                                                }
                                            }}
                                            onError={(e) => {
                                                console.log(`Video ${i} error:`, e.target.error);
                                            }}
                                        />
                                    ) : null}

                                    {showAltVideo && altVideoSrc ? (
                                        <video
                                            ref={el => altVideoRefs.current[i] = el}
                                            className="c-splash__slide__video c-splash__slide__video--alt"
                                            src={altVideoSrc}
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                            onCanPlay={() => {
                                                console.log(`Alt video ${i} can play`);
                                                if (i === active) {
                                                    playActiveVideo();
                                                }
                                            }}
                                            onError={(e) => {
                                                console.log(`Alt video ${i} error:`, e.target.error);
                                            }}
                                        />
                                    ) : null}
                                    
                                    {/* Always show image as fallback or primary */}
                                    <Image 
                                        src={imageSrc} 
                                        alt={slide.alt || `Slide ${i + 1}`}
                                        className={`c-splash__slide__image ${showVideo && videoSrc ? 'c-splash__slide__image--fallback' : ''}`}
                                    />

                                    {altImageSrc ? (
                                        <Image 
                                            src={altImageSrc} 
                                            alt={slide.alt || `Slide ${i + 1}`}
                                            className={`c-splash__slide__image c-splash__slide__image--alt ${showAltVideo && altVideoSrc ? 'c-splash__slide__image--fallback' : ''}`}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default PageSplash;
